import React from 'react';
import { useTranslation } from 'react-i18next';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { BdsIllustration, BdsButton } from '@/core/constants/webComponents';
import { ClassificationCard } from '../ClassificationCard';
import './ClassificationCardList.scss';

export type ClassificationCardListStateType = 'noResults' | 'loadingTime' | 'offline' | 'error';

interface ClassificationCardListProps {
  posts: Post[];
  stateType?: ClassificationCardListStateType;
}

const ILLUSTRATION_MAP: Record<ClassificationCardListStateType, string> = {
  noResults: 'search-1',
  loadingTime: 'time-1',
  offline: 'robot-6',
  error: 'atention',
};

const ACTIONS_ENABLED = new Set<ClassificationCardListStateType>(['loadingTime', 'offline', 'error']);

export const ClassificationCardList: React.FC<ClassificationCardListProps> = ({
  posts,
  stateType = 'noResults',
}) => {
  const { t } = useTranslation();

  const handleReload = () => {
    globalThis.location.reload();
  };

  if (posts.length === 0) {
    const illustrationName = ILLUSTRATION_MAP[stateType];
    const titleKey = `classificationWall.emptyState.${stateType}.title`;
    const descriptionKey = `classificationWall.emptyState.${stateType}.description`;
    const buttonKey = `classificationWall.emptyState.${stateType}.button`;
    const isOffline = stateType === 'offline' && !navigator.onLine;
    const showActions = ACTIONS_ENABLED.has(stateType);

    return (
      <div
        className={`classification-card-list classification-card-list--empty${isOffline ? ' classification-card-list--offline' : ''}`}
        aria-live="polite"
      >
        {!isOffline && (
          <span className="classification-card-list__empty-icon">
            <BdsIllustration
              type="default"
              name={illustrationName}
              alt={t(titleKey)}
              class="classification-card-list__empty-illustration"
            />
          </span>
        )}
        <div className="classification-card-list__empty-message">
          <h2 className="classification-card-list__empty-title">{t(titleKey)}</h2>
          <p className="classification-card-list__empty-description">{t(descriptionKey)}</p>
          {showActions && (
            <div className="classification-card-list__empty-actions">
              <BdsButton onClick={handleReload} variant="primary">
                {t(buttonKey)}
              </BdsButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="classification-card-list">
      {posts.map((post) => (
        <ClassificationCard
          key={post.pid ?? post._id}
          post={post}
        />
      ))}
    </div>
  );
};
